import { AuthHint, AuthScheme } from '@global/api/types'
import { useQuery } from '@tanstack/react-query'
import { enableRefreshToken, redirectOnLogout } from 'api'
import { getAccountAuthHint } from '@global/api'
import { useMemo } from 'react'
import { useLocation } from 'react-use'

const KEY = 'accountAuthHint'

export const useForceRegularLogin = () => {
  const location = useLocation()

  return useMemo(() => {
    const queryParams = new URLSearchParams(location.search)

    return queryParams.get('login') === 'system'
  }, [location.search])
}

export const useAuthHintQuery = () => {
  const forceRegularLogin = useForceRegularLogin()

  return useQuery({
    queryKey: [KEY],
    queryFn: ({ signal }) => {
      const cached = sessionStorage.getItem(KEY)

      if (cached) {
        return JSON.parse(cached) as AuthHint
      }

      return getAccountAuthHint({ signal })
    },
    onSuccess: response => {
      enableRefreshToken(response.auth_scheme !== AuthScheme.dsc_universal_login)
      redirectOnLogout(response.auth_scheme !== AuthScheme.legacy_without_sso && !forceRegularLogin)

      sessionStorage.setItem(KEY, JSON.stringify(response))
    },
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60, // 1 hour
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

export const useSsoEnabled = () => {
  const { data: authHint } = useAuthHintQuery()
  const forceRegularLogin = useForceRegularLogin()

  return useMemo(
    () => authHint?.auth_scheme === AuthScheme.legacy_with_sso && !forceRegularLogin,
    [authHint?.auth_scheme, forceRegularLogin],
  )
}
